import axios from 'axios';
import { getDelivery, recordDelivery, reviewDelivery } from './deliveryApi';
import { bulkIssue, bulkSummary, deliveryShape, saveBulkBatch } from '../utils/bulkDelivery';
const active = new Set();
export const getBulkInvoices = async config => {
  const { data } = await axios.get('/api/running', config);
  if (!Array.isArray(data)) throw new Error('Unexpected invoice list.');
  return data;
};
export async function limitedMap(entries, task, limit = 2) {
  const results = new Array(entries.length); let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(limit, entries.length) }, async () => {
    while (cursor < entries.length) { const index = cursor++; results[index] = await task(entries[index], index); }
  }));
  return results;
}
export async function prepareBulkInvoices(invoices) {
  return limitedMap(invoices, async invoice => {
    try {
      const doc = await getDelivery(invoice._id); const issue = String(doc._id) !== String(invoice._id) ? 'Invoice identity did not match the requested invoice.' : bulkIssue(doc);
      return { invoiceId: invoice._id, invoiceNumber: doc.invoiceNumber || invoice.invoiceNumber, doc, issue };
    } catch (error) { return { invoiceId: invoice._id, invoiceNumber: invoice.invoiceNumber, issue: error.response?.data?.error || 'Unable to refresh invoice.' }; }
  });
}
const requestId = () => (typeof window !== 'undefined' ? window.crypto?.randomUUID?.() : null) || `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export async function processBulkDelivery(batch, onResult = () => {}, persist = saveBulkBatch) {
  if (active.has(batch.id)) throw new Error('This batch is already processing.');
  active.add(batch.id);
  try {
    await limitedMap(batch.entries.filter(entry => entry.status !== 'success' && entry.retryReady !== false), async entry => {
      let doc;
      try {
        entry.status = 'processing'; entry.error = ''; persist(batch);
        // A single-item attempt has its own confirmation/date. Do not adopt it.
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`delivery-attempt:${entry.invoiceId}`)) throw new Error('Resolve the pending delivery in the individual Delivery panel first.');
        const check = current => {
          const issue = bulkIssue(current);
          if (issue) throw new Error(issue);
          if (String(current._id) !== String(entry.invoiceId) || deliveryShape(current) !== entry.shape) throw new Error('Invoice items or organization changed; inspect and confirm a new selection.');
        };
        doc = await getDelivery(entry.invoiceId); check(doc);
        if (bulkSummary(doc).status === 'Fully Delivered' && !entry.attempt) { entry.status = 'success'; entry.alreadyComplete = true; persist(batch); onResult({ entry, doc, success: true }); return; }
        if (entry.attempt) {
          const attempt = entry.attempt;
          const recorded = (doc.deliveryHistory || []).find(row => row.requestId === attempt.requestId);
          if (recorded && (String(recorded.itemId).toLowerCase() !== String(attempt.itemId).toLowerCase() || recorded.quantity !== attempt.quantity || new Date(recorded.date).getTime() !== new Date(attempt.date).getTime() || (recorded.notes || '') !== attempt.notes)) throw new Error('Saved delivery request differs from server history; manual review required.');
          if (!recorded && bulkSummary(doc).status !== 'Fully Delivered') { doc = await recordDelivery(entry.invoiceId, attempt); check(doc); }
          else if (!recorded) entry.alreadyComplete = true;
          entry.attempt = null; persist(batch);
        }
        for (const itemId of doc.items.map(item => item._id)) {
          doc = await getDelivery(entry.invoiceId); check(doc);
          const item = doc.items.find(row => String(row._id).toLowerCase() === String(itemId).toLowerCase());
          const remaining = item.qty - (item.deliveredQty ?? 0);
          if (!remaining) continue;
          entry.attempt = { itemId: item._id, quantity: remaining, date: batch.date, notes: 'Bulk full delivery confirmed by staff.', requestId: requestId() };
          persist(batch);
          doc = await recordDelivery(entry.invoiceId, entry.attempt); check(doc);
          entry.attempt = null; persist(batch);
        }
        doc = await getDelivery(entry.invoiceId); check(doc);
        if (bulkSummary(doc).remaining !== 0) throw new Error('Remaining quantities changed; review and retry.');
        if (bulkSummary(doc).status !== 'Fully Delivered') {
          doc = await reviewDelivery(entry.invoiceId, { deliveryRevision: doc.deliveryRevision, items: doc.items.map(item => ({ itemId: item._id, deliveredQty: item.qty })) });
        }
        doc = await getDelivery(entry.invoiceId); check(doc);
        if (bulkSummary(doc).status !== 'Fully Delivered') throw new Error('Backend did not confirm completed delivery and review.');
        entry.status = 'success'; entry.error = ''; persist(batch); onResult({ entry, doc, success: true });
      } catch (error) {
        // Even a lost response may have committed. Never resubmit here. Retry
        // starts with a fresh GET and reconciles/replays the exact requestId.
        if (error.response?.status >= 400 && error.response.status < 500 && ![408, 429].includes(error.response.status)) entry.attempt = null;
        entry.status = 'failed'; entry.error = error.response?.data?.error || error.message || 'Unconfirmed delivery outcome. Refresh and retry safely.';
        try { persist(batch); } catch { entry.error += ' Browser recovery storage failed; inspect the individual invoice before retrying.'; }
        try { doc = await getDelivery(entry.invoiceId); } catch { /* Keep the explicit failure and recoverable attempt. */ }
        onResult({ entry, doc, success: false });
      }
    });
    return batch;
  } finally { active.delete(batch.id); }
}
